// components/views/profile/ProfilePage.jsx
import React, { Component } from 'react';
import { HTTPService } from '../../../services/httpService';
import PaymentSetup from './PaymentSetup';

const API = new HTTPService();

class ProfilePage extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      user: this.props.userId,
      installer: this.props.entityId,
      loading: true,
      error: null,
      onboardingStep: 'overview', // overview, payment, complete
      paymentSetupStatus: 'pending' // pending, in_progress, complete
    };
  }

  async componentDidMount() {
    await this.loadUserProfile();
    this.checkOnboardingStatus();
  }

  loadUserProfile = async () => {
    try {
      // Get user and installer information
      const userResponse = await API.get(`/profile/${this.state.user}`);
      const installerResponse = await API.get(`/installers/${this.state.installer}`);
      
      this.setState({
        user: userResponse.data[0],
        installer: installerResponse.data[0],
        loading: false
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      this.setState({
        error: 'Failed to load profile information',
        loading: false
      });
    }
  };

  checkOnboardingStatus = () => {
    const { installer } = this.state;
    
    if (!installer) return;

    // Determine current onboarding step based on installer status
    if (installer.stripe_account_id && installer.onboarding_complete) {
      this.setState({ 
        onboardingStep: 'complete',
        paymentSetupStatus: 'complete'
      });
    } else if (installer.stripe_account_id && !installer.onboarding_complete) {
      this.setState({ 
        onboardingStep: 'payment',
        paymentSetupStatus: 'in_progress'
      });
    } else {
      this.setState({ 
        onboardingStep: 'payment',
        paymentSetupStatus: 'pending'
      });
    }
  };

  handlePaymentSetupComplete = () => {
    this.setState({ 
      onboardingStep: 'complete',
      paymentSetupStatus: 'complete'
    });
    this.loadUserProfile(); // Refresh data
  };

  render() {
    const { user, installer, loading, error, onboardingStep, paymentSetupStatus } = this.state;

    console.log('ProfilePage state:', this.state);

    if (loading) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3">Loading profile...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="alert alert-danger">
                <h4>Error Loading Profile</h4>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={this.loadUserProfile}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-header">
              <h1>Profile Settings</h1>
              <p className="text-muted">Manage your account and business information</p>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left Column - Main Content */}
          <div className="col-md-8">
            
            {/* Trial Specialist Onboarding Section */}
            {!!installer.is_trial && (
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Complete Your Account Setup</h4>
                </div>
                <div className="card-body">
                  
                  {/* Onboarding Progress */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-weight-bold">Setup Progress</span>
                      <span className="text-muted">
                        {onboardingStep === 'complete' ? '100%' : '50%'}
                      </span>
                    </div>
                    <div className="progress">
                      <div 
                        className="progress-bar bg-success" 
                        style={{
                          width: onboardingStep === 'complete' ? '100%' : '50%'
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Onboarding Steps */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '30px', height: '30px'}}>
                            ✓
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Account Created</h6>
                          <p className="text-muted mb-0">Your trial account is active</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center ${
                            paymentSetupStatus === 'complete' ? 'bg-success text-white' : 'bg-warning text-dark'
                          }`} style={{width: '30px', height: '30px'}}>
                            {paymentSetupStatus === 'complete' ? '✓' : '2'}
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Payment Setup</h6>
                          <p className="text-muted mb-0">
                            {paymentSetupStatus === 'complete' ? 'Connected' : 'Required to receive payments'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action */}
                  {paymentSetupStatus !== 'complete' && (
                    <div className="alert alert-warning">
                      <h6 className="alert-heading">Payment Setup Required</h6>
                      <p className="mb-2">You must connect your bank account before completing any jobs.</p>
                      <p className="mb-0">This enables you to receive your job payments instantly.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Setup Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h4 className="mb-0">Payment Account</h4>
              </div>
              <div className="card-body">
                <PaymentSetup 
                  installer={installer}
                  onSetupComplete={this.handlePaymentSetupComplete}
                />
              </div>
            </div>

            {/* Business Information Section (Placeholder) */}
            <div className="card mb-4">
              <div className="card-header">
                <h4 className="mb-0">Business Information</h4>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <p className="mb-0">Business information management coming soon. Contact support for changes.</p>
                </div>
                
                {/* Current Information Display */}
                {installer && (
                  <div className="row">
                    <div className="col-md-6">
                      <strong>Business Name:</strong><br />
                      {installer.Installer_Name || 'Not set'}
                    </div>
                    <div className="col-md-6">
                      <strong>Email:</strong><br />
                      {installer.installer_email || user.Email}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account Settings Section (Placeholder) */}
            <div className="card mb-4">
              <div className="card-header">
                <h4 className="mb-0">Account Settings</h4>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <p className="mb-0">Account preferences and security settings coming soon.</p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column - Status & Quick Info */}
          <div className="col-md-4">
            
            {/* Account Status Card */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Account Status</h5>
              </div>
              <div className="card-body">
                {installer && (
                  <>
                    <div className="mb-3">
                      <strong>Account Type:</strong><br />
                      <span className={`badge ${installer.is_trial ? 'badge-warning' : 'badge-success'}`}>
                        {installer.is_trial ? 'Trial Specialist' : 'Active Specialist'}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <strong>Subscription:</strong><br />
                      {installer.subscription || 'ASSIST'}
                    </div>
                    
                    <div className="mb-3">
                      <strong>Payment Status:</strong><br />
                      <span className={`badge ${installer.onboarding_complete ? 'badge-success' : 'badge-warning'}`}>
                        {installer.onboarding_complete ? 'Setup Complete' : 'Setup Required'}
                      </span>
                    </div>

                    {!!installer.is_trial ? (
                      <div className="mb-3">
                        <strong>Trial Jobs:</strong><br />
                        {installer.trial_jobs_completed || 0} completed
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Support Card */}
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Need Help?</h5>
              </div>
              <div className="card-body">
                <p>Having trouble with setup or have questions?</p>
                <a href="mailto:support@jobvious.com" className="btn btn-outline-primary btn-sm">
                  Contact Support
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

export default ProfilePage;